import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react'
import { trackPortfolioCrossLink } from '../analytics/portfolioCrossLink'
import { cn } from './utils'

export interface PortfolioNavLink {
  key: string
  label: string
  href: string
  disabled?: boolean
  current?: boolean
}

export interface PortfolioNavProps {
  brandLabel: string
  brandHref: string
  products: PortfolioNavLink[]
  items: PortfolioNavLink[]
  sourceToolKey?: string
  actions?: ReactNode
  renderLink?: (link: PortfolioNavLink, className: string, children: ReactNode) => ReactNode
}

function defaultRenderLink(link: PortfolioNavLink, className: string, children: ReactNode) {
  return (
    <a href={link.href} className={className}>
      {children}
    </a>
  )
}

function normalizePathname(pathname: string): string {
  return pathname.replace(/\/+$/, '') || '/'
}

function isCurrentHref(href: string): boolean {
  if (typeof window === 'undefined' || href.includes('#')) return false

  try {
    const url = new URL(href, window.location.origin)
    const hrefPathname = normalizePathname(url.pathname)
    const currentPathname = normalizePathname(window.location.pathname)

    return currentPathname === hrefPathname
      || (hrefPathname !== '/' && currentPathname.startsWith(`${hrefPathname}/`))
  } catch {
    return false
  }
}

function scrollCurrentLinkIntoView(container: HTMLDivElement | null) {
  if (!container) return

  const explicitCurrent = container.querySelector<HTMLElement>('[data-current="true"]')
  const pathCurrent = Array.from(
    container.querySelectorAll<HTMLElement>('[data-nav-href]'),
  ).find((element) => isCurrentHref(element.dataset.navHref ?? ''))
  const current = explicitCurrent ?? pathCurrent

  current?.scrollIntoView({ block: 'nearest', inline: 'center' })
}

export function PortfolioNav({
  brandLabel,
  brandHref,
  products,
  items,
  sourceToolKey,
  actions,
  renderLink = defaultRenderLink,
}: PortfolioNavProps) {
  const brandLink: PortfolioNavLink = {
    key: 'brand',
    label: brandLabel,
    href: brandHref,
  }
  const currentProduct = products.find((product) => product.current)
  const productRowRef = useRef<HTMLDivElement | null>(null)
  const itemRowRef = useRef<HTMLDivElement | null>(null)
  const [mobileItemLabel, setMobileItemLabel] = useState(
    () => items.find((item) => item.current)?.label ?? 'Разделы',
  )
  const [mobileItemsOpen, setMobileItemsOpen] = useState(false)

  useEffect(() => {
    const currentItem = items.find((item) => item.current)
      ?? items.find((item) => isCurrentHref(item.href))
    const nextMobileItemLabel = currentItem?.label ?? 'Разделы'

    setMobileItemLabel((currentLabel) => (
      currentLabel === nextMobileItemLabel ? currentLabel : nextMobileItemLabel
    ))
    scrollCurrentLinkIntoView(productRowRef.current)
    scrollCurrentLinkIntoView(itemRowRef.current)
  })

  return (
    <header className="border-b bg-card shadow-sm">
      <nav className="container relative mx-auto px-4 py-2.5" aria-label="Основная навигация">
        <div className="flex min-w-0 items-center gap-2">
          {renderLink(
            brandLink,
            'flex-none text-base font-bold tracking-normal text-foreground hover:text-primary',
            brandLabel,
          )}

          <div
            className="hidden lg:order-none lg:block lg:w-auto lg:min-w-0 lg:flex-none"
            aria-label="Проекты"
          >
            <div
              ref={productRowRef}
              className="flex w-full max-w-full min-w-0 flex-wrap items-center gap-1 lg:flex-nowrap lg:overflow-x-auto lg:px-1 lg:[scrollbar-width:none] lg:[&::-webkit-scrollbar]:hidden"
            >
              {products.map((product) => (
                <a
                  key={product.key}
                  href={product.href}
                  aria-current={product.current ? 'page' : undefined}
                  data-current={product.current ? 'true' : undefined}
                  data-nav-href={product.href}
                  onClick={(event) => {
                    trackProductNavigation(product, currentProduct, sourceToolKey, event)
                  }}
                  onAuxClick={(event) => {
                    if (event.button === 1) {
                      trackProductNavigation(product, currentProduct, sourceToolKey, event)
                    }
                  }}
                  className={cn(
                    'flex-none snap-start whitespace-nowrap rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                    product.current
                      ? 'border-foreground/20 bg-foreground text-background'
                      : 'border-border bg-background text-muted-foreground hover:bg-accent hover:text-foreground',
                  )}
                >
                  {product.label}
                </a>
              ))}
            </div>
          </div>

          <details
            open={mobileItemsOpen}
            onToggle={(event) => {
              setMobileItemsOpen(event.currentTarget.open)
            }}
            className="group ml-auto min-w-0 max-w-[65%] lg:hidden"
          >
            <summary
              aria-controls="portfolio-tool-switcher"
              aria-label={`Выбрать инструмент, текущий: ${mobileItemLabel}`}
              className="flex min-h-9 min-w-0 cursor-pointer list-none items-center gap-2 rounded-md border bg-background px-3 py-1.5 text-left text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 [&::-webkit-details-marker]:hidden"
            >
              <span className="min-w-0 truncate">{mobileItemLabel}</span>
              <span
                aria-hidden="true"
                className="h-2 w-2 flex-none rotate-45 border-b border-r border-muted-foreground transition-transform group-open:rotate-[225deg]"
              />
            </summary>

            <div
              id="portfolio-tool-switcher"
              aria-label="Инструменты"
              className="absolute inset-x-4 z-50 mt-2 max-h-[calc(100dvh-5rem)] overflow-y-auto overscroll-contain rounded-md border bg-background p-2 shadow-lg"
            >
              <div className="grid gap-1">
                {items.map((item) => {
                  const className = cn(
                    'block rounded-md px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                    item.current
                      ? 'bg-accent text-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                    item.disabled && 'cursor-not-allowed opacity-40 hover:bg-transparent',
                  )

                  if (item.disabled) {
                    return (
                      <span
                        key={item.key}
                        aria-current={item.current ? 'page' : undefined}
                        aria-disabled="true"
                        className={className}
                      >
                        {item.label}
                      </span>
                    )
                  }

                  return (
                    <span
                      key={item.key}
                      aria-current={item.current ? 'page' : undefined}
                      onClick={() => {
                        setMobileItemsOpen(false)
                      }}
                    >
                      {renderLink(item, className, item.label)}
                    </span>
                  )
                })}
              </div>

              <div className="mt-2 border-t px-1 pt-2" aria-label="Проекты">
                <div className="grid grid-cols-3 gap-1">
                  {products.map((product) => (
                    <a
                      key={product.key}
                      href={product.href}
                      aria-current={product.current ? 'page' : undefined}
                      onClick={(event) => {
                        setMobileItemsOpen(false)
                        trackProductNavigation(product, currentProduct, sourceToolKey, event)
                      }}
                      onAuxClick={(event) => {
                        if (event.button === 1) {
                          trackProductNavigation(product, currentProduct, sourceToolKey, event)
                        }
                      }}
                      className={cn(
                        'rounded-md px-2 py-2 text-center text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                        product.current
                          ? 'bg-accent text-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                      )}
                    >
                      {product.label}
                    </a>
                  ))}
                </div>
              </div>

              {actions && (
                <div className="mt-2 flex flex-wrap items-center gap-2 border-t px-1 pt-2">
                  {actions}
                </div>
              )}
            </div>
          </details>

          <div
            className="hidden min-w-0 lg:flex lg:w-auto lg:flex-1"
            aria-label="Разделы"
          >
            <div
              ref={itemRowRef}
              className="flex w-full max-w-full min-w-0 snap-x items-center gap-1 overflow-x-auto scroll-px-6 px-6 [scrollbar-width:none] lg:px-0 [&::-webkit-scrollbar]:hidden"
            >
              {items.map((item) => {
                const className = cn(
                  'flex-none snap-start whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors',
                  item.current
                    ? 'bg-accent text-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                  item.disabled && 'cursor-not-allowed opacity-40 hover:bg-transparent',
                )

                if (item.disabled) {
                  return (
                    <span
                      key={item.key}
                      aria-disabled="true"
                      data-current={item.current ? 'true' : undefined}
                      data-nav-href={item.href}
                      className={className}
                    >
                      {item.label}
                    </span>
                  )
                }

                return (
                  <span
                    key={item.key}
                    data-current={item.current ? 'true' : undefined}
                    data-nav-href={item.href}
                    className="flex-none snap-start"
                  >
                    {renderLink(item, className, item.label)}
                  </span>
                )
              })}
            </div>
          </div>

          {actions && (
            <div className="hidden flex-none items-center gap-2 lg:flex">
              {actions}
            </div>
          )}
        </div>
      </nav>
    </header>
  )
}

function trackProductNavigation(
  targetProduct: PortfolioNavLink,
  currentProduct: PortfolioNavLink | undefined,
  sourceToolKey: string | undefined,
  event: MouseEvent<HTMLAnchorElement>,
): void {
  if (typeof window === 'undefined') return

  try {
    const target = new URL(targetProduct.href, window.location.href)
    if (target.hostname === window.location.hostname) return

    trackPortfolioCrossLink({
      sourceApp: currentProduct?.key || window.location.hostname.split('.')[0] || 'unknown',
      sourceDomain: window.location.hostname,
      sourceRoute: window.location.pathname,
      sourceSurface: 'portfolio_nav',
      targetApp: targetProduct.key,
      targetDomain: target.hostname,
      targetRoute: target.pathname,
      linkReason: 'generic_nav',
      targetUrl: targetProduct.href,
      sourceToolKey,
      locale: typeof document === 'undefined'
        ? undefined
        : document.documentElement.lang || undefined,
    }, event.nativeEvent)
  } catch {
    // A malformed target or analytics failure must not affect the anchor click.
  }
}
